import { useLayoutEffect, useRef } from "react"
import { replaceMacroTokensInString } from "authoring/routes/macro/lib/macro-text-utils"
import { htmlToText } from "lib/html-to-text"
import { useBoundContext } from "lib/@components/binding/use-bound-context"

/**
 * Wraps any subtree and expands $TOKEN$ patterns in the rendered HTML.
 * Works on deeply nested components — only the mount point needs macro awareness.
 * For preview/read-only use only.
 *
 * Content is trusted authored HTML from the SFG20 platform, not user-supplied input.
 *
 * @param {Object} props
 * @param {Object} [props.macros] - macro map keyed by token ID (falls back to macroMap from bound context)
 * @param {boolean} [props.plainText] - when true, convert HTML macro content to formatted plain text
 * @param {React.ReactNode} props.children - any React subtree
 */
export function MacroExpander({ macros: macrosProp, plainText = false, children }) {
    const { macroMap } = useBoundContext()
    const macros = macrosProp ?? macroMap
    const ref = useRef()
    const macrosRef = useRef(macros)
    const expanding = useRef(false)
    macrosRef.current = macros

    useLayoutEffect(expandAndObserve, [macros, plainText])

    return (
        <div ref={ref} style={{ display: "contents" }}>
            {children}
        </div>
    )

    function expandAndObserve() {
        if (!ref.current || !macros || !Object.keys(macros).length) return undefined
        expandContainer(ref.current, macrosRef.current, expanding, plainText)

        const observer = new MutationObserver(handleMutations)
        observer.observe(ref.current, { childList: true, subtree: true })
        return () => observer.disconnect()
    }

    function handleMutations() {
        if (!ref.current || expanding.current) return
        expandContainer(ref.current, macrosRef.current, expanding, plainText)
    }
}

/**
 * Walks text nodes in the container and expands $TOKEN$ patterns.
 * Operates on text nodes so it works at any nesting depth without
 * clobbering surrounding element structure. When a macro expands to
 * rich HTML the text node is replaced with parsed fragment nodes,
 * unless plainText mode is active — then HTML is converted to
 * nicely formatted plain text first.
 *
 * Content is trusted authored HTML from the SFG20 platform.
 */
function expandContainer(container, macros, expanding, plainText) {
    expanding.current = true
    try {
        const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, skipScriptStyle)
        const textNodes = []
        while (walker.nextNode()) textNodes.push(walker.currentNode)

        for (const node of textNodes) {
            const original = node.nodeValue
            if (!original || !original.includes("$")) continue

            const expanded = replaceMacroTokensInString({
                textOrHtml: original,
                macrosById: macros,
            })
            if (expanded === original) continue

            if (plainText || expanded === stripHtml(expanded)) {
                // Plain text result or plainText mode — convert any HTML to readable text
                node.nodeValue = plainText ? htmlToText(expanded, true) : expanded
            } else {
                // Rich HTML result — replace text node with parsed fragment
                const template = document.createElement("template")
                template.innerHTML = expanded // trusted authored content
                node.parentNode.replaceChild(template.content, node)
            }
        }
    } finally {
        expanding.current = false
    }
}

function skipScriptStyle(node) {
    const tag = node.parentElement?.tagName
    if (tag === "SCRIPT" || tag === "STYLE") return NodeFilter.FILTER_REJECT
    return NodeFilter.FILTER_ACCEPT
}

function stripHtml(str) {
    return str.replace(/<[^>]*>/g, "")
}
